import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { isStoreHost } from "@/store/nav";
import { setCustomerTokenAccess } from "@/lib/api";

const KEY = "sl_consent_v1";
const Ctx = createContext(null);
const OFF = { necessary: false, functional: false, analytics: false, advertising: false };
const ESSENTIAL_ONLY = { ...OFF, necessary: true };

function isStoreSurface() {
  return isStoreHost || window.location.pathname.startsWith("/store");
}

function savedChoice() {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) || "null");
    if (saved?.decision && saved?.categories) return saved;
  } catch {
    return null;
  }
  return null;
}

export function ConsentProvider({ children }) {
  const [state, setState] = useState(() => {
    if (!isStoreSurface()) return { ready: true, decision: "internal", categories: { ...OFF, necessary: true } };
    const saved = savedChoice();
    return saved
      ? { ready: true, ...saved, categories: { ...saved.categories, necessary: true } }
      : { ready: true, decision: null, categories: OFF };
  });
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const active = isStoreSurface();
  const canUseNecessary = !active || state.categories.necessary;
  const canUseFunctional = !active || (state.categories.necessary && state.categories.functional);
  useEffect(() => {
    setCustomerTokenAccess(canUseNecessary);
  }, [canUseNecessary]);
  const choose = (decision, categories) => {
    const next = {
      decision,
      categories: { ...categories, necessary: true },
      updated_at: new Date().toISOString(),
    };
    localStorage.setItem(KEY, JSON.stringify(next));
    setState({ ready: true, ...next });
    setPreferencesOpen(false);
  };
  const value = useMemo(() => ({
    ...state,
    active,
    canUseNecessary,
    canUseFunctional,
    preferencesOpen,
    openPreferences: () => setPreferencesOpen(true),
    closePreferences: () => setPreferencesOpen(false),
    acceptAll: () => choose("accepted", { necessary: true, functional: true, analytics: true, advertising: true }),
    rejectAll: () => choose("rejected", ESSENTIAL_ONLY),
    savePreferences: (categories) => choose("custom", categories),
  }), [active, canUseFunctional, canUseNecessary, preferencesOpen, state]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useConsent = () => useContext(Ctx);